'use client';

import { loadSearchAnalyticsActions } from '@coveo/headless-react/ssr';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import { useEffect, useRef } from 'react';
import { useSearchEngine } from './SearchDefinitionContext';

type HydratedEngine = NonNullable<ReturnType<typeof useSearchEngine>>;

/**
 * Gates Coveo usage analytics on OneTrust functional consent for the hydrated
 * SSR engine — the SSR counterpart of SearchPageLayout's live toggle. The
 * engine is built with analytics disabled (engineDefinition), so nothing is
 * tracked until consent is read as granted; a later OneTrust change toggles
 * it live.
 *
 * It also logs the page's interface-load search event, once per hydrated
 * engine. The server-side search runs with analytics disabled, and hydration
 * only replays its result without an analytics action, so without this the
 * landing search would log no UA search event and every result click would
 * reference a searchUid Coveo never saw. Logging from the hydrated state
 * reuses the server search's searchUid, so later clicks join to it. Consent
 * granted mid-visit logs it then, keeping those clicks attributable.
 *
 * Renders inside the StateProvider; the engine is undefined in the static
 * phase, so this no-ops until hydration.
 */
export function AnalyticsConsentSync() {
  const engine = useSearchEngine();
  const { functionalCategoryEnabled } = useCookieSettings();
  const interfaceLoadLoggedFor = useRef<HydratedEngine>();

  useEffect(() => {
    if (!engine) {
      return;
    }
    if (!functionalCategoryEnabled) {
      engine.disableAnalytics();
      return;
    }
    engine.enableAnalytics();
    if (interfaceLoadLoggedFor.current !== engine) {
      interfaceLoadLoggedFor.current = engine;
      engine.dispatch(loadSearchAnalyticsActions(engine).logInterfaceLoad());
    }
  }, [engine, functionalCategoryEnabled]);

  return null;
}
